import type { BuildProgressStatus } from '@/entities/build.entity';
import { createEvent, createStore, sample } from 'effector';
import { $resetAuth } from './profile-entity';

export type BuildLogStream = 'stdout' | 'stderr' | 'log';

export interface IBuildLogLine {
	stream: BuildLogStream;
	text: string;
	ts: number;
}

export interface IBuildLiveProgress {
	buildId: string;
	stage: string | null;
	progressStatus: BuildProgressStatus | null;
	message?: string;
	ts: number;
}

export interface IBuildCompleteEvent {
	buildId: string;
	success: boolean;
	errorMessage?: string | null;
	ts: number;
}

const MAX_LOG_LINES = 2000;

export const $resetBuildLive = createEvent();
export const $updateBuildProgress = createEvent<IBuildLiveProgress>();
export const $appendBuildLogs = createEvent<{ buildId: string; lines: IBuildLogLine[] }>();
export const $setBuildLogHistory = createEvent<{ buildId: string; lines: IBuildLogLine[] }>();
export const $clearBuildLive = createEvent<string>();
export const $setBuildComplete = createEvent<IBuildCompleteEvent>();

export const $buildSawDropperStage = createStore<Record<string, boolean>>({})
	.on($updateBuildProgress, (state, payload) => {
		if (payload.stage !== 'dropper') {
			return state;
		}
		return { ...state, [payload.buildId]: true };
	})
	.on($clearBuildLive, (state, buildId) => {
		const next = { ...state };
		delete next[buildId];
		return next;
	})
	.reset($resetBuildLive);

export const $buildSawPayloadEncryptStage = createStore<Record<string, boolean>>({})
	.on($updateBuildProgress, (state, payload) => {
		if (payload.stage !== 'payload_encrypt') {
			return state;
		}
		return { ...state, [payload.buildId]: true };
	})
	.on($clearBuildLive, (state, buildId) => {
		const next = { ...state };
		delete next[buildId];
		return next;
	})
	.reset($resetBuildLive);

export function selectSawDropperStage(
	state: Record<string, boolean>,
	buildId: string | null | undefined,
): boolean {
	if (!buildId) return false;
	return state[buildId] ?? false;
}

export function selectSawPayloadEncryptStage(
	state: Record<string, boolean>,
	buildId: string | null | undefined,
): boolean {
	if (!buildId) return false;
	return state[buildId] ?? false;
}

export const $buildLiveProgress = createStore<Record<string, IBuildLiveProgress>>({})
	.on($updateBuildProgress, (state, payload) => ({ ...state, [payload.buildId]: payload }))
	.on($clearBuildLive, (state, buildId) => {
		const next = { ...state };
		delete next[buildId];
		return next;
	})
	.reset($resetBuildLive);

export const $buildLogs = createStore<Record<string, IBuildLogLine[]>>({})
	.on($appendBuildLogs, (state, { buildId, lines }) => {
		const existing = state[buildId] ?? [];
		const merged = [...existing, ...lines];
		return { ...state, [buildId]: merged.length > MAX_LOG_LINES ? merged.slice(-MAX_LOG_LINES) : merged };
	})
	.on($setBuildLogHistory, (state, { buildId, lines }) => ({ ...state, [buildId]: lines }))
	.on($clearBuildLive, (state, buildId) => {
		const next = { ...state };
		delete next[buildId];
		return next;
	})
	.reset($resetBuildLive);

export const $lastBuildComplete = createStore<IBuildCompleteEvent | null>(null)
	.on($setBuildComplete, (_, payload) => payload)
	.reset($resetBuildLive);

sample({
	clock: $resetAuth,
	target: $resetBuildLive,
});
